import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, IconButton, Tooltip, Box, FormControlLabel, Switch, Checkbox } from '@mui/material';
import { useSalonsListQuery, useSalonsCategoriesListQuery, useServicesPartialUpdateMutation, useAuthProfileRetrieveQuery, useServicesSalonListQuery } from '../../../../services/GLOWClient';
import theme from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import EditIcon from '@mui/icons-material/Edit';
import { parseDurationString, formatDuration } from '../../../../utils/formatters';
import { BASE_URL } from '../../../../config';
import UpdateCategory from '../category/updateCategory';
import ServiceConfirmationModeField from './ServiceConfirmationModeField';

const EditService = ({ open, onClose, onSuccess, serviceData }) => {
    const { t, i18n } = useTranslation();
    // Helper function to build image URL - identical to the one in service.jsx
    const buildImageUrl = (url) => {
        if (!url) return '';
        
        // If URL is already absolute, return as is
        if (/^https?:\/\//i.test(url)) {
            // Check if it's a localhost URL without port and add port 8012
            if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
                return url.replace('http://localhost/', 'http://localhost:8012/');
            }
            return url;
        }
        
        // For relative URLs that start with /media/
        if (url.startsWith('/media/')) {
            // Use port 8012 for localhost in development
            const baseUrl = window.location.hostname === 'localhost' ? 
                'http://localhost:8012' : 
                BASE_URL.replace(/\/$/, ''); // Remove trailing slash
            return `${baseUrl}${url}`;
        }
        
        // For relative URLs that start with media/ (no leading slash)
        if (url.startsWith('media/')) {
            // Use port 8012 for localhost in development
            const baseUrl = window.location.hostname === 'localhost' ? 
                'http://localhost:8012' : 
                BASE_URL.replace(/\/$/, ''); // Remove trailing slash
            return `${baseUrl}/${url}`;
        }
        
        // For other relative URLs, prepend BASE_URL
        const baseUrl = window.location.hostname === 'localhost' ? 
            'http://localhost:8012' : 
            BASE_URL.replace(/\/$/, ''); // Remove trailing slash
        const cleanUrl = url.startsWith('/') ? url : `/${url}`;
        return `${baseUrl}${cleanUrl}`;
    };
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        salon: '',
        category: '',
        price: '',
        prix_fixe: true,
        client_booking_disabled: false,
        booking_confirmation_mode: 'automatic',
        duration: '',
        durationDisplay: '',
        images: [],
        existingImages: [],
        primaryImageIndex: 0,
        removeImages: [],
        setPrimaryImage: null
    });
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // State for category editing
    const [isUpdateCategoryDialogOpen, setIsUpdateCategoryDialogOpen] = useState(false);
    const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
    
    // Get current user profile to filter salons by owner
    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    
    // Fetch salons owned by the current user
    const { data: salonsData, isLoading: salonsLoading } = useSalonsListQuery(
        userProfile ? { owner: userProfile.id } : { skip: true }
    );
    const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();

    const getTranslatedField = useCallback((item, field) => {
        const translationsKey = `${field}_translations`;
        const localizedKey = `localized_${field}`;
        const translations = item?.[translationsKey] || {};

        if (translations[currentLanguage]) return translations[currentLanguage];
        if (item?.[localizedKey]) return item[localizedKey];

        for (const fallbackLanguage of ['fr', 'en', 'ar']) {
            if (translations[fallbackLanguage]) return translations[fallbackLanguage];
        }

        return item?.[field] || '';
    }, [currentLanguage]);

    const normalizeBoolean = (value, fallback = false) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') return value.toLowerCase() === 'true';
        if (value === undefined || value === null || value === '') return fallback;
        return Boolean(value);
    };

    const shouldUpdateRawField = (item, field) => {
        const rawValue = item?.[field] || '';
        const currentTranslatedValue = item?.[`${field}_translations`]?.[currentLanguage]
            || item?.[`localized_${field}`]
            || '';

        return !currentTranslatedValue || currentTranslatedValue === rawValue;
    };
    
    // Fetch categories for the selected salon only
    const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useSalonsCategoriesListQuery(
        formData.salon ? { salonId: Number(formData.salon), language: i18n.language } : { salonId: 0 },
        { skip: !formData.salon }
    );
    
    // Fetch services for the selected salon for validation/reference
    const { data: salonServicesData, isLoading: salonServicesLoading } = useServicesSalonListQuery(
        formData.salon ? { salonId: Number(formData.salon), language: i18n.language } : undefined,
        { skip: !formData.salon }
    );
    
    const [updateService, { isLoading: isUpdating }] = useServicesPartialUpdateMutation();
    
    // Initialize form with service data when it changes
    useEffect(() => {
        if (serviceData) {
            // Initialize basic form data
            const initialFormData = {
                name: getTranslatedField(serviceData, 'name'),
                description: getTranslatedField(serviceData, 'description'),
                // Ensure salon is a numeric ID even if an object was provided
                salon: (() => {
                    const s = serviceData.salon;
                    const sid = serviceData.salon_id;
                    const value = sid ?? (typeof s === 'object' && s ? (s.salon_id ?? s.id) : s);
                    return value !== undefined && value !== null && value !== '' ? Number(value) : '';
                })(),
                // Ensure category is a numeric ID even if an object was provided
                category: (() => {
                    const c = serviceData.category;
                    const cid = serviceData.category_id;
                    const value = cid ?? (typeof c === 'object' && c ? (c.category_id ?? c.id) : c);
                    return value !== undefined && value !== null && value !== '' ? Number(value) : '';
                })(),
                price: serviceData.price || '',
                prix_fixe: normalizeBoolean(serviceData.prix_fixe, true),
                client_booking_disabled: normalizeBoolean(serviceData.client_booking_disabled),
                booking_confirmation_mode: serviceData.booking_confirmation_mode || 'automatic',
                duration: serviceData.duration || '',
                durationDisplay: serviceData.duration ? formatDuration(serviceData.duration) : '',
                images: [],
                existingImages: [],
                primaryImageIndex: 0,
                removeImages: [],
                setPrimaryImage: null
            };
           
            // Process images if they exist
            if (serviceData.images && serviceData.images.length > 0) {
                
                // Find the primary image
                const primaryImage = serviceData.images.find(img => img.is_primary);
                const primaryIndex = primaryImage ? serviceData.images.indexOf(primaryImage) : 0;
                
                // Set image previews
                const previews = serviceData.images.map(img => {
                    return buildImageUrl(img.image_url);
                });
                setImagePreviews(previews);
                
                // Add existing images to form data
                initialFormData.existingImages = serviceData.images.map(img => ({
                    id: img.image_id,
                    url: buildImageUrl(img.image_url),
                    isPrimary: img.is_primary
                }));
                initialFormData.primaryImageIndex = primaryIndex;
            } else {
                setImagePreviews([]);
            }
            
            // Set the complete form data
            setFormData(initialFormData);
        }
    }, [getTranslatedField, serviceData]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'durationDisplay') {
            // Parse duration string to minutes
            const durationInMinutes = parseDurationString(value);
            setFormData(prev => ({
                ...prev,
                durationDisplay: value,
                duration: durationInMinutes
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        // Clear error when field is edited
        if (errors[name] || (name === 'durationDisplay' && errors.duration)) {
            setErrors(prev => ({
                ...prev,
                [name === 'durationDisplay' ? 'duration' : name]: ''
            }));
        };
    };
    
    // Handle category edit
    const handleEditCategory = (category) => {
        setSelectedCategoryForEdit(category);
        setIsUpdateCategoryDialogOpen(true);
    };
    
    // Handle category update success
    const handleCategoryUpdateSuccess = () => {
        setIsUpdateCategoryDialogOpen(false);
        setSelectedCategoryForEdit(null);
        // Refetch categories to get updated data
        refetchCategories();
        // Also call the parent's onSuccess to trigger any additional refreshes
        if (onSuccess) {
            onSuccess(null, null); // Call without service data to just trigger refresh
        }
    };
    
    const handleMultipleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), ...files]
            }));
            
            // Create preview URLs for all files
            files.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreviews(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            });
            
            // Clear error if exists
            if (errors.images) {
                setErrors(prev => ({
                    ...prev,
                    images: ''
                }));
            }
        }
    };
    
    const removeNewImage = (index) => {
        setFormData(prev => {
            const newImages = [...(prev.images || [])];
            newImages.splice(index, 1);
            
            // Adjust primary image index if needed for new images
            let newPrimaryIndex = prev.primaryImageIndex || 0;
            if (index === newPrimaryIndex) {
                newPrimaryIndex = newImages.length > 0 ? 0 : -1;
            } else if (index < newPrimaryIndex) {
                newPrimaryIndex--;
            }
            
            return {
                ...prev,
                images: newImages,
                primaryImageIndex: newPrimaryIndex
            };
        });
        
        setImagePreviews(prev => {
            const newPreviews = [...prev];
            newPreviews.splice(index, 1);
            return newPreviews;
        });
    };
    
    const removeExistingImage = (imageId) => {
        setFormData(prev => ({
            ...prev,
            removeImages: [...(prev.removeImages || []), imageId]
        }));
    };
    
    const setPrimaryImage = (imageId) => {
        setFormData(prev => ({
            ...prev,
            setPrimaryImage: imageId
        }));
    };
    
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = t('pro_service.edit_service.name_required_error');
        // Salon is automatically set from service data, no validation needed
        if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
            newErrors.price = t('pro_service.edit_service.price_valid_error');
        }
        if (!formData.duration || isNaN(formData.duration) || parseInt(formData.duration) <= 0) {
            newErrors.duration = t('pro_service.edit_service.duration_valid_error');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async () => {
        if (!validateForm()) return;
        
        setIsSubmitting(true);
        
        try {
            // Create FormData object for file upload
            const formDataObj = new FormData();
            const trimmedName = formData.name.trim();
            const trimmedDescription = formData.description?.trim() || '';
            const shouldUpdateRawName = shouldUpdateRawField(serviceData, 'name');
            const shouldUpdateRawDescription = shouldUpdateRawField(serviceData, 'description');

            if (shouldUpdateRawName) {
                formDataObj.append('name', trimmedName);
            }

            if (!shouldUpdateRawName || serviceData?.name_translations?.[currentLanguage]) {
                formDataObj.append('name_translations', JSON.stringify({
                    ...(serviceData?.name_translations || {}),
                    [currentLanguage]: trimmedName
                }));
            }

            formDataObj.append('salon', Number(formData.salon));
            formDataObj.append('price', parseFloat(formData.price));
            formDataObj.append('prix_fixe', String(!!formData.prix_fixe));
            formDataObj.append('client_booking_disabled', String(!!formData.client_booking_disabled));
            formDataObj.append('booking_confirmation_mode', formData.booking_confirmation_mode);
            formDataObj.append('duration', parseInt(formData.duration));
            
            if (formData.description !== undefined) {
                if (shouldUpdateRawDescription) {
                    formDataObj.append('description', trimmedDescription);
                }

                if (!shouldUpdateRawDescription || serviceData?.description_translations?.[currentLanguage]) {
                    formDataObj.append('description_translations', JSON.stringify({
                        ...(serviceData?.description_translations || {}),
                        [currentLanguage]: trimmedDescription
                    }));
                }
            }
            
            if (formData.category) {
                formDataObj.append('category', Number(formData.category));
            } else {
                formDataObj.append('category', '');
            }
            
            // Handle multiple new images
            if (formData.images && formData.images.length > 0) {
                formData.images.forEach((image, index) => {
                    formDataObj.append('images', image);
                });
                formDataObj.append('primary_image_index', formData.primaryImageIndex);
            }
            
            // Handle removing existing images
            if (formData.removeImages && formData.removeImages.length > 0) {
                formData.removeImages.forEach(imageId => {
                    formDataObj.append('remove_images', imageId);
                });
            }
            
            // Handle setting primary image
            if (formData.setPrimaryImage !== null) {
                formDataObj.append('set_primary_image', formData.setPrimaryImage);
            }
            
            // Send formData directly as the body for file upload
            const updateData = {
                serviceId: serviceData.service_id || serviceData.id,
                body: formDataObj
            };
            
            const result = await updateService(updateData).unwrap();
            
            // Create a service object to pass to the success callback
            const updatedService = {
                id: serviceData.id,
                name: shouldUpdateRawName ? trimmedName : (serviceData?.name || trimmedName),
                description: shouldUpdateRawDescription ? trimmedDescription : (serviceData?.description || trimmedDescription),
                name_translations: (!shouldUpdateRawName || serviceData?.name_translations?.[currentLanguage])
                    ? {
                        ...(serviceData?.name_translations || {}),
                        [currentLanguage]: trimmedName
                    }
                    : (serviceData?.name_translations || {}),
                description_translations: (!shouldUpdateRawDescription || serviceData?.description_translations?.[currentLanguage])
                    ? {
                        ...(serviceData?.description_translations || {}),
                        [currentLanguage]: trimmedDescription
                    }
                    : (serviceData?.description_translations || {}),
                ...result
            };
            
            // Call success callback with the updated service data
            onSuccess && onSuccess(updatedService);
            onClose();
        } catch (error) {
            console.error('Failed to update service:', error);
            
            // Handle API validation errors
            if (error.data && error.data.errors) {
                setErrors(error.data.errors);
            } else if (error.data) {
                // Handle non-field errors or other error formats
                const apiErrors = {};
                
                if (error.data.name) apiErrors.name = error.data.name.join(' ');
                if (error.data.salon) apiErrors.salon = error.data.salon.join(' ');
                if (error.data.category) apiErrors.category = error.data.category.join(' ');
                if (error.data.description) apiErrors.description = error.data.description.join(' ');
                if (error.data.price) apiErrors.price = error.data.price.join(' ');
                if (error.data.duration) apiErrors.duration = error.data.duration.join(' ');
                if (error.data.images) apiErrors.images = error.data.images.join(' ');
                if (error.data.non_field_errors) {
                    // Show non-field errors in the name field for visibility
                    apiErrors.name = error.data.non_field_errors.join(' ');
                }
                
                setErrors(apiErrors);
            }
            
            // Call onSuccess with error to display notification
            onSuccess && onSuccess(null, error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Reset dialog state when closed
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
            setErrors({});
        } else {
            setDialogClosed(false);
        }
    }, [open]);

    // Form content to be displayed in the dialog
    const formContent = (
        <>
            <TextField
                fullWidth
                label={t('pro_service.edit_service.service_name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
                disabled={isSubmitting || isUpdating}
            />
            
            <TextField
                fullWidth
                label={t('pro_service.edit_service.description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                disabled={isSubmitting || isUpdating}
            />
            
            {/* Salon is automatically set from the service data - no dropdown needed */}
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1, alignItems: 'flex-end' }}>
                <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.category}
                    disabled={isUpdating || categoriesLoading}
                >
                    <InputLabel>{t('pro_service.edit_service.category')}</InputLabel>
                    <Select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        label={t('pro_service.edit_service.category')}
                    >
                        <MenuItem value="">{t('pro_service.edit_service.no_category')}</MenuItem>
                        {categoriesLoading ? (
                            <MenuItem disabled>{t('pro_service.edit_service.loading_categories')}</MenuItem>
                        ) : categoriesData && categoriesData.length > 0 ? (
                            (() => {
                                const options = [...categoriesData];
                                const currentId = Number(formData.category);
                                const hasCurrent = options.some(c => Number((c.category_id ?? c.id)) === currentId);
                                if (!hasCurrent && formData.category) {
                                    // Use name from serviceData.category_details if available; otherwise fallback to ID label
                                    const fallbackName = (serviceData && serviceData.category_details)
                                        ? getTranslatedField(serviceData.category_details, 'name')
                                        : t('pro_service.edit_service.category_number', { id: formData.category });
                                    options.unshift({ category_id: currentId, name: t('pro_service.edit_service.current_category', { name: fallbackName }) });
                                }
                                return options.map(category => (
                                    <MenuItem key={category.category_id ?? category.id} value={category.category_id ?? category.id}>
                                        {getTranslatedField(category, 'name')}
                                    </MenuItem>
                                ));
                            })()
                        ) : (
                            <MenuItem disabled>{t('pro_service.edit_service.no_category_available')}</MenuItem>
                        )}
                    </Select>
                    {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                </FormControl>
                
                {formData.category && categoriesData && (
                    <Tooltip title={t('pro_service.edit_service.edit_category_tooltip')} arrow>
                        <IconButton
                            onClick={() => {
                                const selectedCategory = categoriesData.find(c => 
                                    Number(c.category_id ?? c.id) === Number(formData.category)
                                );
                                if (selectedCategory) {
                                    handleEditCategory(selectedCategory);
                                }
                            }}
                            disabled={isSubmitting || isUpdating}
                            sx={{
                                marginBottom: '8px',
                                padding: '8px',
                                borderRadius: '8px',
                                backgroundColor: theme.colors.primary[50],
                                color: theme.colors.primary[600],
                                border: `1px solid ${theme.colors.primary[200]}`,
                                '&:hover': {
                                    backgroundColor: theme.colors.primary[100],
                                    transform: 'scale(1.05)',
                                },
                                '&:disabled': {
                                    backgroundColor: theme.colors.grey[100],
                                    color: theme.colors.grey[400],
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <EditIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                <TextField
                    label={t('pro_service.edit_service.price')}
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    margin="normal"
                    type="number"
                    inputProps={{ min: 0, step: '100' }}
                    error={!!errors.price}
                    helperText={errors.price}
                    disabled={isSubmitting || isUpdating}
                    fullWidth
                />
                
                <TextField
                    label={t('pro_service.edit_service.duration')}
                    name="durationDisplay"
                    value={formData.durationDisplay}
                    onChange={handleChange}
                    margin="normal"
                    type="text"
                    placeholder={t('pro_service.edit_service.duration_placeholder')}
                    error={!!errors.duration}
                    helperText={errors.duration || t('pro_service.edit_service.duration_formats')}
                    disabled={isSubmitting || isUpdating}
                    fullWidth
                />
            </Box>

            <FormControlLabel
                control={
                    <Switch
                        checked={!!formData.prix_fixe}
                        onChange={(e) => setFormData(prev => ({ ...prev, prix_fixe: e.target.checked }))}
                        disabled={isSubmitting || isUpdating}
                    />
                }
                label={t('pro_service.edit_service.fixed_price')}
                sx={{ mt: 1 }}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={!!formData.client_booking_disabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, client_booking_disabled: e.target.checked }))}
                        disabled={isSubmitting || isUpdating}
                    />
                }
                label={t('pro_service.edit_service.disable_client_booking')}
                sx={{
                    mt: 0.5,
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                        lineHeight: 1.4,
                    },
                }}
            />

            <Box sx={{ mt: 2 }}>
                <ServiceConfirmationModeField
                    value={formData.booking_confirmation_mode}
                    onChange={(value) => setFormData(prev => ({ ...prev, booking_confirmation_mode: value }))}
                    disabled={isSubmitting || isUpdating}
                    hidden={!!formData.client_booking_disabled}
                    labels={{
                        title: t('appointment_workflow.service_confirmation.title'),
                        automatic: t('appointment_workflow.service_confirmation.automatic'),
                        manual: t('appointment_workflow.service_confirmation.manual'),
                    }}
                />
            </Box>
            
            <div style={{ marginTop: '16px' }}>
                
                {/* Display existing images */}
                {formData.existingImages && formData.existingImages.length > 0 && (
                    <div style={{ marginBottom: '15px' }}>
                        <h5 style={{ marginBottom: '8px' }}>{t('pro_service.edit_service.existing_images')}</h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {formData.existingImages.map((img, index) => {
                                // Skip images that are marked for removal
                                if (formData.removeImages && formData.removeImages.includes(img.id)) {
                                    return null;
                                }
                                
                                const isPrimary = formData.setPrimaryImage === img.id || 
                                    (formData.setPrimaryImage === null && img.isPrimary);
                                
                                return (
                                    <div 
                                        key={img.id} 
                                        style={{ 
                                            position: 'relative',
                                            border: isPrimary
                                                ? `2px solid ${theme.colors.primary[600]}`
                                                : `1px solid ${theme.colors.border.light}`,
                                            borderRadius: '8px',
                                            padding: '3px'
                                        }}
                                    >
                                        <img 
                                            src={img.url} 
                                            alt={t('pro_service.edit_service.image_alt', { index: index + 1 })} 
                                            style={{ 
                                                width: '120px', 
                                                height: '120px', 
                                                objectFit: 'cover',
                                                borderRadius: '6px'
                                            }} 
                                        />
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => removeExistingImage(img.id)}
                                            disabled={isSubmitting || isUpdating}
                                            sx={{ 
                                                minWidth: '30px',
                                                width: '30px',
                                                height: '30px',
                                                padding: 0,
                                                position: 'absolute',
                                                top: '5px',
                                                right: '5px',
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                                }
                                            }}
                                        >
                                            ×
                                        </Button>
                                        {!isPrimary && (
                                            <Button
                                                size="small"
                                                color="primary"
                                                onClick={() => setPrimaryImage(img.id)}
                                                disabled={isSubmitting || isUpdating}
                                                sx={{ 
                                                    minWidth: '30px',
                                                    width: '30px',
                                                    height: '30px',
                                                    padding: 0,
                                                    position: 'absolute',
                                                    top: '5px',
                                                    left: '5px',
                                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.95)',
                                                    }
                                                }}
                                                title={t('pro_service.edit_service.set_as_primary')}
                                            >
                                                ★
                                            </Button>
                                        )}
                                        {isPrimary && (
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '5px',
                                                left: '5px',
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                padding: '2px 5px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: theme.colors.primary[600]
                                            }}>
                                                {t('pro_service.edit_service.primary')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                    
                {/* Add new images */}
                <div>
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="service-multiple-images-edit"
                        type="file"
                        onChange={handleMultipleImagesChange}
                        disabled={isSubmitting || isUpdating}
                        multiple
                    />
                    <label htmlFor="service-multiple-images-edit">
                        <Button
                            variant="outlined"
                            component="span"
                            disabled={isSubmitting || isUpdating}
                            sx={{
                                borderColor: theme.colors.primary[400],
                                color: theme.colors.primary[600],
                                '&:hover': {
                                    borderColor: theme.colors.primary[600],
                                    backgroundColor: theme.colors.primary[50],
                                }
                            }}
                        >
                            {t('pro_service.edit_service.add_images')}
                        </Button>
                    </label>
                    {errors.images && (
                        <FormHelperText error>{errors.images}</FormHelperText>
                    )}
                </div>
                    
                {/* Display new images to be added */}
                {formData.images && formData.images.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                        <h5 style={{ marginBottom: '8px' }}>{t('pro_service.edit_service.new_images')}</h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {formData.images.map((_, index) => {
                                // Calculate the correct preview index
                                const previewIndex = index;
                                const preview = imagePreviews[previewIndex];
                                if (!preview) return null;
                                
                                // Determine if this is the primary image
                                const isPrimary = index === formData.primaryImageIndex;
                                
                                return (
                                    <div 
                                        key={`new-${index}`} 
                                        style={{ 
                                            position: 'relative',
                                            border: isPrimary 
                                                ? `2px solid ${theme.colors.primary[600]}` 
                                                : `1px solid ${theme.colors.border.light}`,
                                            borderRadius: '8px',
                                            padding: '3px'
                                        }}
                                    >
                                        <img 
                                            src={preview} 
                                            alt={t('pro_service.edit_service.new_image_alt', { index: index + 1 })} 
                                            style={{ 
                                                width: '120px', 
                                                height: '120px', 
                                                objectFit: 'cover',
                                                borderRadius: '6px'
                                            }} 
                                        />
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => removeNewImage(index)}
                                            disabled={isSubmitting || isUpdating}
                                            sx={{ 
                                                minWidth: '30px',
                                                width: '30px',
                                                height: '30px',
                                                padding: 0,
                                                position: 'absolute',
                                                top: '5px',
                                                right: '5px',
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                                }
                                            }}
                                        >
                                            ×
                                        </Button>
                                        {!isPrimary && (
                                            <Button
                                                size="small"
                                                color="primary"
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    primaryImageIndex: index
                                                }))}
                                                disabled={isSubmitting || isUpdating}
                                                sx={{ 
                                                    minWidth: '30px',
                                                    width: '30px',
                                                    height: '30px',
                                                    padding: 0,
                                                    position: 'absolute',
                                                    top: '5px',
                                                    left: '5px',
                                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.95)',
                                                    }
                                                }}
                                                title={t('pro_service.edit_service.set_as_primary')}
                                            >
                                                ★
                                            </Button>
                                        )}
                                        {isPrimary && (
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '5px',
                                                left: '5px',
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                padding: '2px 5px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: theme.colors.primary[600]
                                            }}>
                                                {t('pro_service.edit_service.primary')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <>
            <MuiDialog
                open={open}
                icon={<EditIcon />}
                triggerButton={triggerButton}
                title={t('pro_service.edit_service.edit_service_title')}
                subtitle={t('pro_service.edit_service.edit_service_subtitle')}
                content={formContent}
                existAction={true}
                actionTitle={t('pro_service.edit_service.save')}
                actionFunction={handleSubmit}
                onClose={dialogClosed}
                onExternalClose={onClose}
                isLoading={isSubmitting || isUpdating}
                actionType="button"
                maxWidth="sm"
                disableAction={isSubmitting || isUpdating}
            />
            
            {/* Update Category Dialog */}
            {selectedCategoryForEdit && (
                <UpdateCategory
                    open={isUpdateCategoryDialogOpen}
                    onClose={() => {
                        setIsUpdateCategoryDialogOpen(false);
                        setSelectedCategoryForEdit(null);
                    }}
                    onSuccess={handleCategoryUpdateSuccess}
                    categoryData={selectedCategoryForEdit}
                    salonId={formData.salon}
                />
            )}
        </>
    );
};

export default EditService;
